import { mintSegmentId, segmentKey } from './segments.js';
import { type ViewerId } from './viewer.js';

/**
 * Belief layer (`03-information-model.md §9`, ADR-0005 §8). A belief is a Fact
 * owned by a non-canon Viewer that may contradict canon, projecting only into
 * that Viewer's perspective.
 *
 * **ADR-0005 §8 — belief is the `owner`, not a key discriminator.** A belief is
 * now a **valid-time segment whose `owner` is a non-canon Viewer**. The op-log
 * `#` slot carries the segment id (`seg_…`); the believer is read off the
 * fact's `owner: belief(viewer)`. This composes with segments (the slot can no
 * longer be hijacked by a `belief:` prefix) and collapses the old
 * `corrected_at` field into a belief-segment `to` ("believed until day N" is
 * the same primitive as "Aria ruled until 1066").
 *
 * Two facts coexist on the same `(subject, base-predicate)` because they live
 * on different segment chains (`<base>#seg_…`) and are distinguished by owner
 * at projection time — exactly how canon segments coexist, now owner-scoped.
 *
 * Identity note: a belief is recognised by `owner.kind === 'belief'`, NEVER by
 * a key suffix. The pre-ADR-0005 `<base>#belief:<viewer>` keying is removed.
 */

/**
 * Mint the op-log predicate key for a NEW belief segment on `basePredicate`.
 * The believer is carried on the fact payload's `owner: belief(viewer)`, not
 * in the key — the key is an ordinary segment key so the belief folds and
 * resolves through the same segment machinery as canon (ADR-0005 §1/§8).
 *
 * `viewer` is accepted for call-site symmetry with the authoring site and to
 * keep the believer explicit there, but it does NOT influence the key.
 */
export function beliefSegmentKey(basePredicate: string, _viewer: ViewerId): string {
  return segmentKey(basePredicate, mintSegmentId());
}
